import {Box, Grid} from "@mui/material";
import React, {FC} from "react";
import * as z from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import TextField from "@mui/material/TextField";
import {useGlobalActions} from "@/store/global.c";
import {QueryObserverResult, RefetchOptions} from "@tanstack/react-query";
import {DialogFooter} from "@/widget/DialogModal/DialogModal";

import {useCreateInvoicing} from "@/service/invoice.s";
import {emptyValue} from "@/helper/method";

export interface WriteInvoicingProps {
    instanceId: number;
    businessId?: number;
    executeId: string;
    refetchExecutes: (options?: RefetchOptions) => Promise<QueryObserverResult>
}

const validationSchema = z.object({
    instanceId: z.number(),
    businessId: z.number().optional(),
    executeId: z.string(),
    invoiceNumber: z.string({message: "请填写发票号码"}).nonempty({message: "请填写发票金额"}),
    invoiceTax: z.string({message: "请填写发票金额"}).nonempty({message: "请填写发票金额"}),
    invoiceContent: z.string(),
});

export type WriteInvoicingForm = z.infer<typeof validationSchema>
const defaultValues = emptyValue(validationSchema)

export const WriteInvoicing: FC<WriteInvoicingProps> = ({instanceId, executeId, businessId, refetchExecutes}) => {
    const {mutateAsync: createInvoicingMutate, isPending: createInvoicingPending} = useCreateInvoicing();

    const {
        handleSubmit,
        register,
        reset,
        formState: {errors},
    } = useForm<WriteInvoicingForm>({
        resolver: zodResolver(validationSchema),
        values: {...defaultValues, businessId, instanceId, executeId},
    });
    const {setToast} = useGlobalActions()
    const {setDialogModal} = useGlobalActions()

    const onSubmit = async (values: WriteInvoicingForm) => {
        const result = await createInvoicingMutate({...values, businessId, instanceId, executeId})
        if (result.code == 0) {
            setToast({message: result.msg, severity: 'success', open: true, duration: 2000});
            await refetchExecutes()
            setDialogModal({open: false})
        } else {
            setToast({message: result.msg, open: true, severity: 'error', duration: 2000});
        }
    }

    return <Box component={'form'} display={'flex'} flexDirection={'column'} rowGap={2} noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid spacing={2} container width={'inherit'}>
            <Grid size={6}>
                <TextField
                    {...register('invoiceNumber')}
                    placeholder="发票号码"
                    error={!!errors?.invoiceNumber}
                    helperText={errors?.invoiceNumber?.message}
                    fullWidth
                />
            </Grid>
            <Grid size={6}>
                <TextField
                    {...register('invoiceTax')}
                    type={'number'}
                    error={!!errors?.invoiceTax}
                    helperText={errors?.invoiceTax?.message}
                    placeholder="发票金额"
                    fullWidth
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    multiline
                    rows={3}
                    slotProps={{
                        input: {
                            sx: {height: 80},
                            inputProps: {maxLength: 250},
                        },
                    }}
                    {...register('invoiceContent')}
                    placeholder="开票内容"
                    fullWidth
                />
            </Grid>
        </Grid>
        <DialogFooter onReset={() => reset(defaultValues)} loading={createInvoicingPending}/>
    </Box>
}